import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {
  CompetitionService,
  eCaseAnimations,
  ECaseDataTable,
  ECaseDateFormatterPipe,
  ECaseSnackBarService,
  ECaseTranslationLoaderService,
  ECaseUtils,
  ParametersService
} from 'synto-common';
import {MatTableDataSource} from '@angular/material/table';
import {AddEcaseApprovalCycleDialogComponent} from './ecase-add-approval-cycle-dialog/ecase-add-approval-cycle-dialog.component';
import cloneDeep from 'lodash/cloneDeep';

@Component({
  selector: 'ecase-manage-approval-cycle-dialog',
  templateUrl: './ecase-manage-approval-cycle-dialog.component.html',
  animations: eCaseAnimations
})
export class ManageEcaseApprovalCycleDialogComponent extends ECaseDataTable implements OnInit {
  displayedColumns = ['description', 'formattedLastUpdateDate', 'lastUpdateBy', 'action'];
  filterObject = {
    description: '',
    formattedLastUpdateDate: '',
    lastUpdateBy: '',
  };
  sortActive = 'description';
  sortDirection = 'asc';
  isLoading = false;
  @Input() approvalCycles = [];
  isUpdatePerformed = false;
  @Input() isCompetitionClosed;
  @Input() cpmCompetition?: number;
  @Input() approvalCycleTypeId?: number;
  private displayedCycles: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<ManageEcaseApprovalCycleDialogComponent>,
    translate: TranslateService,
    parameterService: ParametersService,
    public eCaseSnackBarService: ECaseSnackBarService,
    private eCaseDateFormatterPipe: ECaseDateFormatterPipe,
    public competitionService: CompetitionService,
    private dialog: MatDialog,
    private translationLoader: ECaseTranslationLoaderService,
    private cdr: ChangeDetectorRef) {
    super(translate, parameterService);
  }

  sortColumn(event: any, childPath?: string): void {
    this.sortActive = event.active;
    this.sortDirection = event.direction;
    this.dataSource.data = ECaseUtils.sortColumn(event, this.dataSource.data, this.translate, childPath);
    this.renewDataSource(this.dataSource.data);
  }

  renewDataSource(newDataArray): void {
    this.dataSource = new MatTableDataSource(newDataArray);
    this.sortingDataAccessorWithIgnoreCase();
    this.dataSource.paginator = this.paginator.toArray()[0];
    this.dataSource.sort = this.sort.toArray()[0];
  }

  ngOnInit(): void {
    this.displayedCycles = (this.approvalCycles || []).map((c) => this.normalizeCycleForUi(cloneDeep(c)));
    this.bindCyclesToTable(this.displayedCycles);
    this.translationLoader.loadTranslationsWithObservable(40).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  private bindCyclesToTable(rows: any[]): void {
    this.originalArray = rows;
    this.serviceArray = rows;
    this.renewDataSource(rows);
    this.sortColumn({active: this.sortActive, direction: this.sortDirection});
  }

  private toApprovalCycleArray(approvalCycles: any): any[] {
    if (approvalCycles == null) {
      return [];
    }
    return Array.isArray(approvalCycles) ? approvalCycles : Object.values(approvalCycles);
  }

  private normalizeCycleForUi(cycle: any): any {
    cycle.formattedLastUpdateDate = this.eCaseDateFormatterPipe.transform(cycle.lastUpdateDate, this.syntoDate);
    const labels = cycle.cycleTypeLabel;
    const customDesc = cycle.description != null ? String(cycle.description).trim() : '';
    if (labels && typeof labels === 'object') {
      const lang = this.translate.currentLang || this.translate.getDefaultLang();
      const text = labels[lang] ?? labels.en ?? labels.fr;
      if (text && customDesc === '') {
        cycle.description = text;
      }
    }
    return cycle;
  }

  private reloadCyclesFromApi(): void {
    const cid = this.resolvedCpmCompetitionId();
    const req$ = this.approvalCycleTypeId != null
      ? this.competitionService.getApprovalCyclesByCompetitionIdAndTypeId(cid, this.approvalCycleTypeId)
      : this.competitionService.getApprovalCyclesByCompetitionId(cid, 'all');
    this.isLoading = true;
    this.cdr.markForCheck();
    req$.subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response?.status) {
          this.displayedCycles = this.toApprovalCycleArray(response.approvalCycles)
            .map((c) => this.normalizeCycleForUi(cloneDeep(c)));
          this.bindCyclesToTable(this.displayedCycles);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onEdit(row): void {
    const dialogRef = this.dialog.open(AddEcaseApprovalCycleDialogComponent);
    const instance = dialogRef.componentInstance;
    instance.row = row;
    instance.currentApprovalCycles = cloneDeep(this.displayedCycles);
    instance.cpmCompetition = this.resolvedCpmCompetitionId();
    const fromRow = row?.approvalCycleTypeId ?? row?.cycleTypeId;
    instance.approvalCycleTypeId =
      fromRow != null && fromRow !== '' ? Number(fromRow) : this.approvalCycleTypeId;
    dialogRef.afterClosed().subscribe((decision) => {
      if (decision) {
        this.isUpdatePerformed = true;
        this.reloadCyclesFromApi();
      }
    });
  }

  onDelete(row): void {
    if (row.isDeleteAllowed) {
      const competitionId = this.resolvedCpmCompetitionId();
      const cycleId = Number(row.id);
      const typeRaw = row.approvalCycleTypeId ?? row.cycleTypeId ?? this.approvalCycleTypeId;
      const cycleTypeId = typeRaw != null && typeRaw !== '' ? Number(typeRaw) : NaN;
      if (!competitionId || Number.isNaN(competitionId) || !cycleId || Number.isNaN(cycleId)) {
        this.eCaseSnackBarService.show('failure');
        return;
      }
      const afterDelete = (response: any) => {
        if (response.status) {
          this.isUpdatePerformed = true;
          this.reloadCyclesFromApi();
          this.eCaseSnackBarService.show('success', ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.admindashboard.competitions.awardFinalization.manage.add.cycle.deleted'));
        } else {
          this.eCaseSnackBarService.show('failure', response.errorMessage);
        }
      };
      if (!Number.isNaN(cycleTypeId)) {
        this.competitionService.deleteFullApprovalCycle({
          id: cycleId,
          competitionId,
          cycleTypeId,
        }).subscribe(afterDelete);
      } else {
        this.competitionService.deleteApprovalCycle(row.id, competitionId).subscribe(afterDelete);
      }
    } else {
      this.eCaseSnackBarService.show('failure', ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.admindashboard.competitions.awardFinalization.manage.add.cycle.delete.not.allowed'));
    }
  }

  onCloseDialog(): void {
    this.dialogRef.close(this.isUpdatePerformed);
  }

  private resolvedCpmCompetitionId(): number {
    return this.cpmCompetition != null ? Number(this.cpmCompetition) : Number(this.competitionService.competitionId);
  }
}
